/**
 * Browser Sentry metrics.
 *
 *   resume_count             count         resumeCoordinator resumes after > 30s away (by trigger)
 *   resume_away_duration_ms  distribution  away duration of those resumes (by trigger)
 *   catchup_sync_triggered   count         useSyncVersion catch-ups (by reason, timed_out)
 *   catchup_refetch_duration_ms distribution  catch-up start → refetch settled or 15s cap (same attributes)
 *
 * Does not import the Sentry SDK: browser Sentry loads lazily (instrumentation-client.js) and
 * registers its `metrics` here. Calls made before that are queued (bounded) and replayed.
 */

const MAX_PENDING = 200;

let sink = null;
let pending = [];

function emit(kind, name, value, options) {
  if (sink) {
    try {
      sink[kind](name, value, options);
    } catch {
      /* metrics must never break the app */
    }
    return;
  }
  if (pending.length < MAX_PENDING) pending.push([kind, name, value, options]);
}

/** @param {{ count: Function, gauge: Function, distribution: Function }} metrics */
export function registerMetricsSink(metrics) {
  sink = metrics;
  const queued = pending;
  pending = [];
  for (const [kind, name, value, options] of queued) emit(kind, name, value, options);
}

/** @param {number} awayMs @param {"visibility"|"focus"|"online"|"bfcache"} trigger */
export function recordResume(awayMs, trigger) {
  if (!Number.isFinite(awayMs) || awayMs < 0) return;
  const attributes = { trigger: String(trigger || "unknown") };
  emit("distribution", "resume_away_duration_ms", awayMs, { unit: "millisecond", attributes });
  emit("count", "resume_count", 1, { attributes });
}

/** @param {{ reason: string, durationMs: number, timedOut: boolean }} info */
export function recordCatchUp({ reason, durationMs, timedOut } = {}) {
  if (!Number.isFinite(durationMs) || durationMs < 0) return;
  const attributes = { reason: String(reason || "unknown"), timed_out: String(Boolean(timedOut)) };
  emit("count", "catchup_sync_triggered", 1, { attributes });
  emit("distribution", "catchup_refetch_duration_ms", durationMs, { unit: "millisecond", attributes });
}
